import type { GoalWeekRow } from '../api';
import { Card, CardHeader, Cell, Row, Table } from '../components/Ui';
import { money, shortDate } from '../format';

function signed(value: string | null): string {
  if (value === null) return '—';
  if (value.startsWith('-')) return money(value);
  if (/^0+(\.0+)?$/.test(value)) return money(value);
  return `+${money(value)}`;
}

export function GoalWeekTable({
  weeks,
  asOfWeekNumber,
  compact = false,
}: {
  weeks: readonly GoalWeekRow[];
  asOfWeekNumber: number;
  compact?: boolean;
}) {
  return (
    <Card>
      <CardHeader
        title="Weekly tracker"
        subtitle="Targets, actuals, and remaining pace"
        compact={compact}
      />
      <Table
        compact={compact}
        head={[
          { label: 'Wk' },
          { label: 'Ending' },
          { label: 'Actual', align: 'right' },
          { label: 'Target', align: 'right' },
          { label: 'Variance', align: 'right' },
          { label: 'YTD', align: 'right' },
          { label: 'Avg', align: 'right' },
          { label: 'Remaining', align: 'right' },
          { label: 'Need avg', align: 'right' },
        ]}
      >
        {weeks.map((week) => {
          const past = week.weekNumber <= asOfWeekNumber;
          const varianceTone =
            week.variance === null
              ? 'text-ink-faint'
              : week.variance.startsWith('-')
                ? 'text-red-500'
                : 'text-profit';
          return (
            <Row key={week.weekNumber}>
              <Cell compact={compact}>
                <span
                  className={week.weekNumber === asOfWeekNumber ? 'font-semibold text-ink' : ''}
                >
                  {week.weekNumber}
                  {week.isHoliday ? (
                    <span className="ml-1 text-[10px] uppercase text-ink-faint">hol</span>
                  ) : null}
                </span>
              </Cell>
              <Cell compact={compact} muted>
                {shortDate(week.weekEnding)}
              </Cell>
              <Cell compact={compact} align="right" mono>
                {week.actual === null ? '—' : money(week.actual)}
              </Cell>
              <Cell compact={compact} align="right" mono>
                {money(week.weeklyTarget)}
              </Cell>
              <Cell compact={compact} align="right" mono>
                <span className={varianceTone}>{signed(week.variance)}</span>
              </Cell>
              <Cell compact={compact} align="right" mono>
                {past ? money(week.ytd) : '—'}
              </Cell>
              <Cell compact={compact} align="right" mono>
                {week.ytdAverage === null ? '—' : money(week.ytdAverage)}
              </Cell>
              <Cell compact={compact} align="right" mono>
                {past ? money(week.goalRemaining) : '—'}
              </Cell>
              <Cell compact={compact} align="right" mono>
                {week.needToAverage === null ? '—' : money(week.needToAverage)}
              </Cell>
            </Row>
          );
        })}
      </Table>
    </Card>
  );
}
